import { type ReactNode } from 'react'
import cx from 'classnames'
import { Avatar, type AvatarSubject } from '@/components/Avatar'
import { RawButton } from '@/components/Button'
import { Dropdown } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { Skeleton } from '@/components/Skeleton'
import styles from './NextStepCard.module.css'

export type NextStepTone = 'default' | 'warning' | 'danger' | 'live'

interface NextStepCardProps {
  children: ReactNode
  testId?: string
}

/**
 * Next step card root.
 * Where the job stands and the one thing to do next, with anything blocking
 * that step attached underneath as footers.
 */
export function NextStepCard({ children, testId }: NextStepCardProps) {
  return (
    <section className={styles.root} aria-label="Next step" data-test={testId}>
      {children}
    </section>
  )
}

interface NextStepCardSkeletonProps {
  /**
   * Footers that don't wait on the step, such as a booked call.
   */
  children?: ReactNode
}

/**
 * Holds the card's place while the step it shows loads.
 */
export function NextStepCardSkeleton({ children }: NextStepCardSkeletonProps) {
  return (
    <section className={styles.root} aria-label="Next step" aria-busy>
      <div className={styles.indicator}>
        <Skeleton block variant="rect" height={3} />
      </div>
      <div className={styles.body}>
        <div className={styles.info}>
          <div className={styles.eyebrow}>
            <span>
              <Skeleton width={140} />
            </span>
          </div>
          <div className={styles.title}>
            <span>
              <Skeleton width={240} />
            </span>
          </div>
          <p className={styles.description}>
            <Skeleton width="80%" />
            <Skeleton width="45%" />
          </p>
        </div>
        <div className={styles.actions}>
          <Skeleton
            variant="rect"
            width={112}
            height="var(--control)"
            borderRadius="var(--rounded-md)"
          />
        </div>
      </div>
      {children}
    </section>
  )
}

interface NextStepCardBodyProps {
  children: ReactNode
}

/**
 * Next step card body.
 * Lays out the step's details beside its actions.
 */
export function NextStepCardBody({ children }: NextStepCardBodyProps) {
  return <div className={styles.body}>{children}</div>
}

interface NextStepCardInfoProps {
  children: ReactNode
}

/**
 * Next step card info.
 * Holds the eyebrow, title and description.
 */
export function NextStepCardInfo({ children }: NextStepCardInfoProps) {
  return <div className={styles.info}>{children}</div>
}

interface NextStepCardStepsProps {
  /**
   * One-based position of the current step.
   */
  current: number
  total: number
  tone?: NextStepTone
}

/**
 * Next step card steps.
 * One segment per step of the flow, up to and including the current one
 * filled.
 */
export function NextStepCardSteps({
  current,
  total,
  tone = 'default',
}: NextStepCardStepsProps) {
  return (
    <div className={styles.indicator} aria-hidden="true">
      {Array.from({ length: total }, (_, index) => (
        <span
          key={index}
          className={cx(
            styles.segment,
            index + 1 < current && styles.done,
            index + 1 === current && styles.current,
            index + 1 === current && styles[tone],
          )}
        />
      ))}
    </div>
  )
}

interface NextStepCardProgressProps {
  /**
   * Percentage done.
   */
  done: number
  /**
   * Percentage in progress, drawn after what's done.
   */
  pending: number
}

/**
 * Next step card progress.
 * For stages that track progress as a share rather than as steps.
 */
export function NextStepCardProgress({
  done,
  pending,
}: NextStepCardProgressProps) {
  return (
    <div className={styles.indicator} aria-hidden="true">
      <span className={styles.track}>
        <span className={styles.fill} style={{ width: `${done}%` }} />
        <span className={styles.pending} style={{ width: `${pending}%` }} />
      </span>
    </div>
  )
}

interface NextStepCardEyebrowProps {
  children?: ReactNode
  /**
   * Names a flow that runs beside the job's own, such as an extension.
   */
  tag?: string
}

/**
 * Next step card eyebrow.
 * Where the step falls in the flow and how long the job has sat in it.
 */
export function NextStepCardEyebrow({
  children,
  tag,
}: NextStepCardEyebrowProps) {
  if (!children && !tag) {
    return null
  }

  return (
    <div className={styles.eyebrow}>
      {tag && <span className={styles.tag}>{tag}</span>}
      {children}
    </div>
  )
}

interface NextStepCardTitleProps {
  children: ReactNode
  /**
   * Opens what every step of the flow asks of whom.
   */
  onViewSteps?: () => unknown
  testId?: string
  viewStepsTestId?: string
}

/**
 * Next step card title.
 */
export function NextStepCardTitle({
  children,
  onViewSteps,
  testId,
  viewStepsTestId,
}: NextStepCardTitleProps) {
  return (
    <div className={styles.title}>
      <span data-test={testId}>{children}</span>
      {onViewSteps && (
        <RawButton
          className={styles.iconButton}
          aria-label="About this step and all progression steps"
          onPress={onViewSteps}
          testId={viewStepsTestId}
        >
          <Icon name="info" size={15} aria-hidden="true" />
        </RawButton>
      )}
    </div>
  )
}

interface NextStepCardDescriptionProps {
  children?: ReactNode
}

/**
 * Next step card description.
 * What the step asks of the viewer.
 */
export function NextStepCardDescription({
  children,
}: NextStepCardDescriptionProps) {
  if (!children) {
    return null
  }

  return <p className={styles.description}>{children}</p>
}

interface NextStepCardActionsProps {
  children: ReactNode
}

/**
 * Next step card actions.
 * The step's buttons, primary last.
 */
export function NextStepCardActions({ children }: NextStepCardActionsProps) {
  return <div className={styles.actions}>{children}</div>
}

interface NextStepCardWaitingOnProps {
  subject: AvatarSubject
  label: string
  testId?: string
}

/**
 * Next step card waiting on.
 * Stands in for the actions when the step is someone else's to take.
 */
export function NextStepCardWaitingOn({
  subject,
  label,
  testId,
}: NextStepCardWaitingOnProps) {
  return (
    <span className={styles.waitingOn} data-test={testId}>
      <Avatar subject={subject} size={20} />
      {label}
    </span>
  )
}

interface NextStepCardMoreProps {
  children: ReactNode
  testId?: string
}

/**
 * Next step card more.
 * Actions that stay available on the step without competing with its main
 * one.
 */
export function NextStepCardMore({ children, testId }: NextStepCardMoreProps) {
  return (
    <Dropdown
      autoDismiss
      width="small"
      position="bottom-end"
      button={
        <RawButton className={styles.moreButton} aria-label="More step actions">
          <Icon name="more" size={14} aria-hidden="true" />
        </RawButton>
      }
      testId={testId}
    >
      {children}
    </Dropdown>
  )
}

interface NextStepCardFooterProps {
  children: ReactNode
  tone?: 'warning' | 'live' | 'neutral'
  actions?: ReactNode
  testId?: string
}

/**
 * Next step card footer.
 * Something running beside the step, such as a call or a pending scope
 * change, with its own actions.
 */
export function NextStepCardFooter({
  children,
  tone = 'neutral',
  actions,
  testId,
}: NextStepCardFooterProps) {
  return (
    <div
      className={cx(styles.footer, styles[tone])}
      role="status"
      data-test={testId}
    >
      <div className={styles.footerText}>{children}</div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  )
}

interface NextStepCardCompletedProps {
  title: string
  meta?: string
  onViewSteps?: () => unknown
  testId?: string
  viewStepsTestId?: string
}

/**
 * Next step card completed.
 * The whole card once nothing is left to do.
 */
export function NextStepCardCompleted({
  title,
  meta,
  onViewSteps,
  testId,
  viewStepsTestId,
}: NextStepCardCompletedProps) {
  return (
    <div className={styles.completed}>
      <span className={styles.check}>
        <Icon name="checkmark" size={11} aria-hidden="true" />
      </span>
      <span className={styles.completedTitle} data-test={testId}>
        {title}
      </span>
      {meta && <span className={styles.completedMeta}>{meta}</span>}
      {onViewSteps && (
        <RawButton
          className={styles.link}
          onPress={onViewSteps}
          testId={viewStepsTestId}
        >
          View steps
        </RawButton>
      )}
    </div>
  )
}
